/**
 * @nanokart/tryon-studio-sdk
 * usePhoto hook - Manage customer photo
 */

import { useCallback, useRef } from 'react';
import { useStore } from './useStore';

export interface UsePhotoReturn {
  file: File | null;
  preview: string | null;
  isUploading: boolean;
  error: string | null;
  hasPhoto: boolean;
  setPhoto: (file: File) => void;
  setPhotoFromUrl: (url: string) => void;
  clearPhoto: () => void;
  openFilePicker: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Maximum upload size in MB. Defaults to 20.
 * Consumers can override at build time via `REACT_APP_MAX_UPLOAD_MB` env var.
 * Must stay in sync with backend's `MAX_IMAGE_UPLOAD_MB` setting.
 */
const _envMB = (typeof process !== 'undefined' && process.env && Number(process.env.REACT_APP_MAX_UPLOAD_MB)) || 0;
export const MAX_UPLOAD_MB = _envMB > 0 ? _envMB : 20;
const MAX_FILE_SIZE = MAX_UPLOAD_MB * 1024 * 1024;
const COMPRESSION_THRESHOLD = 1 * 1024 * 1024; // 1MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/** Compress image to below 1MB using canvas. Falls back to original on error. */
async function compressImage(file: File): Promise<File> {
  if (file.size <= COMPRESSION_THRESHOLD) return file;

  return new Promise<File>((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const MAX_DIM = 1920;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const scale = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);

      const QUALITIES = [0.92, 0.85, 0.78, 0.70, 0.62];
      let qi = 0;

      const attempt = () => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          if (blob.size <= COMPRESSION_THRESHOLD || qi >= QUALITIES.length - 1) {
            const name = file.name.replace(/\.[^/.]+$/, '.jpg');
            resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
          } else {
            qi++;
            attempt();
          }
        }, 'image/jpeg', QUALITIES[qi]);
      };

      attempt();
    };

    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
    img.src = objectUrl;
  });
}

export function usePhoto(): UsePhotoReturn {
  const file = useStore((s) => s.photo.file);
  const preview = useStore((s) => s.photo.preview);
  const isUploading = useStore((s) => s.photo.isUploading);
  const error = useStore((s) => s.photo.error);

  const setPhotoFile = useStore((s) => s.setPhotoFile);
  const setPhotoPreview = useStore((s) => s.setPhotoPreview);
  const clearPhotoAction = useStore((s) => s.clearPhoto);
  const showToast = useStore((s) => s.showToast);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasPhoto = Boolean(preview || file);

  const setPhoto = useCallback(
    (photoFile: File) => {
      if (!ALLOWED_TYPES.includes(photoFile.type)) {
        showToast('Please upload a valid image (JPEG, PNG, WebP)', 'error');
        return;
      }
      if (photoFile.size > MAX_FILE_SIZE) {
        showToast(`Image is too large. Maximum size is ${MAX_UPLOAD_MB}MB`, 'error');
        return;
      }

      // Compress if > 1MB before storing (photo is sent to the API when size recommendation is requested)
      compressImage(photoFile).then((compressed) => {
        setPhotoFile(compressed);

        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setPhotoPreview(e.target.result as string);
          }
        };
        reader.readAsDataURL(compressed);
      });
    },
    [setPhotoFile, setPhotoPreview, showToast]
  );

  const setPhotoFromUrl = useCallback(
    (url: string) => {
      setPhotoPreview(url);
    },
    [setPhotoPreview]
  );

  const clearPhoto = useCallback(() => {
    clearPhotoAction();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [clearPhotoAction]);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    file,
    preview,
    isUploading,
    error,
    hasPhoto,
    setPhoto,
    setPhotoFromUrl,
    clearPhoto,
    openFilePicker,
    fileInputRef,
  };
}

